import React, { useState } from 'react';

export default function UpgradeManagement({
  selectedTile,
  userId,
  getCurrentPlayer,
  upgradeTile,
  upgradeEquipment,
  getTileUpgradeLevel,
  getEquipmentUpgradeLevel,
  getUpgradeCost,
  getMaxRoutes,
  getEquipmentMultiplier
}) {
  const [isUpgradingRoutes, setIsUpgradingRoutes] = useState(false);
  const [isUpgradingEquipment, setIsUpgradingEquipment] = useState(false);

  if (!selectedTile || selectedTile.type < 3 || selectedTile.type > 14 || selectedTile.type === 13 || !selectedTile.owner || selectedTile.owner.owner !== userId) {
    return null;
  }

  const currentRouteLevel = getTileUpgradeLevel(selectedTile.key);
  const currentEquipmentLevel = getEquipmentUpgradeLevel ? getEquipmentUpgradeLevel(selectedTile.key) : 0;
  const maxLevel = 3;
  
  const nextRouteCost = getUpgradeCost(currentRouteLevel + 1);
  const nextEquipmentCost = getUpgradeCost(currentEquipmentLevel + 1);
  
  const currentPlayer = getCurrentPlayer();
  const canAffordRoutes = currentPlayer && currentPlayer.budget >= nextRouteCost;
  const canAffordEquipment = currentPlayer && currentPlayer.budget >= nextEquipmentCost;
  
  const canUpgradeRoutes = currentRouteLevel < maxLevel && canAffordRoutes;
  const canUpgradeEquipment = currentEquipmentLevel < maxLevel && canAffordEquipment;
  
  const maxRoutes = getMaxRoutes(currentRouteLevel);
  const equipmentMultiplier = getEquipmentMultiplier ? getEquipmentMultiplier(currentEquipmentLevel) : 1.0;

  const handleRouteUpgrade = async () => {
    if (!canUpgradeRoutes || isUpgradingRoutes) return;
    
    setIsUpgradingRoutes(true);
    try {
      await upgradeTile(selectedTile.key);
    } finally {
      setIsUpgradingRoutes(false);
    }
  };

  const handleEquipmentUpgrade = async () => {
    if (!canUpgradeEquipment || isUpgradingEquipment) return;
    
    setIsUpgradingEquipment(true);
    try {
      await upgradeEquipment(selectedTile.key);
    } finally {
      setIsUpgradingEquipment(false);
    }
  };

  const getRouteBenefits = (level) => {
    const routes = getMaxRoutes(level);
    return `${routes} max routes`;
  };

  const getEquipmentBenefits = (level) => {
    const multipliers = { 0: 1.0, 1: 1.25, 2: 1.5, 3: 2.0 };
    const multiplier = multipliers[level] || 1.0;
    const percentage = ((multiplier - 1) * 100).toFixed(0);
    return percentage === "0" ? "Base efficiency" : `+${percentage}% income & costs`;
  };

  return (
    <div style={{
      marginBottom: '15px',
      padding: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '5px',
      border: '1px solid rgba(255, 255, 255, 0.3)'
    }}>
      <h5 style={{ margin: '0 0 10px 0', fontSize: '0.85em', color: '#E8F5E8', wordWrap: 'break-word' }}>
        🔧 Upgrades
      </h5>
      
      {/* Route Upgrades Section */}
      <div style={{ 
        marginBottom: '15px', 
        padding: '8px', 
        backgroundColor: 'rgba(255, 255, 255, 0.05)', 
        borderRadius: '4px' 
      }}>
        <h6 style={{ margin: '0 0 8px 0', fontSize: '0.8em', color: '#C8E6C9', wordWrap: 'break-word' }}>
          📦 Route Upgrades
        </h6>
        
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '0.75em', color: '#C8E6C9', marginBottom: '5px', wordWrap: 'break-word' }}>
            Current Level: {currentRouteLevel}/3
          </div>
          <div style={{ fontSize: '0.75em', color: '#C8E6C9', marginBottom: '5px', wordWrap: 'break-word' }}>
            Current Capacity: {getRouteBenefits(currentRouteLevel)}
          </div>
        </div>

        {currentRouteLevel < maxLevel && (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '0.75em', color: '#90EE90', marginBottom: '5px', wordWrap: 'break-word' }}>
              Next Upgrade: Level {currentRouteLevel + 1}
            </div>
            <div style={{ fontSize: '0.75em', color: '#90EE90', marginBottom: '5px', wordWrap: 'break-word' }}>
              New Capacity: {getRouteBenefits(currentRouteLevel + 1)}
            </div>
            <div style={{ fontSize: '0.75em', color: '#FFB74D', marginBottom: '8px', wordWrap: 'break-word' }}>
              Cost: ${nextRouteCost.toLocaleString()}
            </div>
            
            <button
              onClick={handleRouteUpgrade}
              disabled={!canUpgradeRoutes || isUpgradingRoutes}
              style={{
                width: '100%',
                padding: '6px 10px',
                backgroundColor: canUpgradeRoutes ? 'rgba(76, 175, 80, 0.8)' : 'rgba(128, 128, 128, 0.5)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                cursor: canUpgradeRoutes ? 'pointer' : 'not-allowed',
                fontSize: '0.75em',
                opacity: isUpgradingRoutes ? 0.7 : 1
              }}
            >
              {isUpgradingRoutes ? 'Upgrading...' : `Upgrade for $${nextRouteCost.toLocaleString()}`}
            </button>
            
            {!canAffordRoutes && (
              <div style={{ 
                fontSize: '0.7em', 
                color: '#FF6B6B', 
                marginTop: '5px',
                textAlign: 'center',
                wordWrap: 'break-word'
              }}>
                Insufficient funds
              </div>
            )}
          </div>
        )}

        {currentRouteLevel >= maxLevel && (
          <div style={{ 
            fontSize: '0.75em', 
            color: '#90EE90',
            textAlign: 'center',
            fontStyle: 'italic',
            wordWrap: 'break-word'
          }}>
            Maximum route level reached!
          </div>
        )}
      </div>

      {/* Equipment Upgrades Section */}
      <div style={{ 
        marginBottom: '10px', 
        padding: '8px', 
        backgroundColor: 'rgba(255, 255, 255, 0.05)', 
        borderRadius: '4px' 
      }}>
        <h6 style={{ margin: '0 0 8px 0', fontSize: '0.8em', color: '#C8E6C9', wordWrap: 'break-word' }}>
          ⚙️ Equipment Upgrades
        </h6>
        
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '0.75em', color: '#C8E6C9', marginBottom: '5px', wordWrap: 'break-word' }}>
            Current Level: {currentEquipmentLevel}/3
          </div>
          <div style={{ fontSize: '0.75em', color: '#C8E6C9', marginBottom: '5px', wordWrap: 'break-word' }}>
            Current Efficiency: {getEquipmentBenefits(currentEquipmentLevel)}
          </div>
        </div>

        {currentEquipmentLevel < maxLevel && (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '0.75em', color: '#90EE90', marginBottom: '5px', wordWrap: 'break-word' }}>
              Next Upgrade: Level {currentEquipmentLevel + 1}
            </div>
            <div style={{ fontSize: '0.75em', color: '#90EE90', marginBottom: '5px', wordWrap: 'break-word' }}>
              New Efficiency: {getEquipmentBenefits(currentEquipmentLevel + 1)}
            </div>
            <div style={{ fontSize: '0.75em', color: '#FFB74D', marginBottom: '8px', wordWrap: 'break-word' }}>
              Cost: ${nextEquipmentCost.toLocaleString()}
            </div>
            
            <button
              onClick={handleEquipmentUpgrade}
              disabled={!canUpgradeEquipment || isUpgradingEquipment}
              style={{
                width: '100%',
                padding: '6px 10px',
                backgroundColor: canUpgradeEquipment ? 'rgba(76, 175, 80, 0.8)' : 'rgba(128, 128, 128, 0.5)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                cursor: canUpgradeEquipment ? 'pointer' : 'not-allowed',
                fontSize: '0.75em',
                opacity: isUpgradingEquipment ? 0.7 : 1
              }}
            >
              {isUpgradingEquipment ? 'Upgrading...' : `Upgrade for $${nextEquipmentCost.toLocaleString()}`}
            </button>
            
            {!canAffordEquipment && (
              <div style={{ 
                fontSize: '0.7em', 
                color: '#FF6B6B', 
                marginTop: '5px',
                textAlign: 'center',
                wordWrap: 'break-word'
              }}>
                Insufficient funds
              </div>
            )}
          </div>
        )}

        {currentEquipmentLevel >= maxLevel && (
          <div style={{ 
            fontSize: '0.75em', 
            color: '#90EE90',
            textAlign: 'center',
            fontStyle: 'italic',
            wordWrap: 'break-word'
          }}>
            Maximum equipment level reached!
          </div>
        )}
      </div>

      <div style={{
        marginTop: '10px',
        fontSize: '0.7em',
        color: 'rgba(255, 255, 255, 0.6)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        paddingTop: '5px'
      }}>
        <div style={{ marginBottom: '3px' }}>💡 Upgrade Info:</div>
        <div>• Route upgrades increase max connections</div>
        <div>• Equipment upgrades boost income & costs</div>
      </div>
    </div>
  );
}
